/**
 * Admin Analytics Screen - MOBILE ONLY
 * 
 * Usage analytics with real data visualization.
 * Shows user activity, growth trends, and system metrics.
 */

import React, { useCallback } from 'react';
import { View, Text, ScrollView, StyleSheet, Pressable, RefreshControl } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useRouter, Redirect } from 'expo-router';
import { useAuthStore } from '../../../store/authStore';
import { useAdmin, UserGrowthPoint } from '../../../hooks/useAdmin';
import { color, text, space, radius, touch, icon, layout, border } from '../../../shared/tokens/mobile';
import { ScreenContainer, SectionHeader, ListCard } from '../../../components/v2/mobile';
import { formatCurrency } from '../../../shared/utils/formatters';

export default function AdminAnalyticsScreen() {
  const router = useRouter();
  const user = useAuthStore((state) => state.user);
  const isAdmin = user?.role === 'admin';
  const { stats, loading, refresh } = useAdmin();

  const handleBack = useCallback(() => router.back(), [router]);

  if (!isAdmin) {
    return <Redirect href="/v2/home" />;
  }

  return (
    <ScreenContainer>
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        refreshControl={<RefreshControl refreshing={loading} onRefresh={refresh} tintColor={color.primary} />}
      >
        <View style={styles.header}>
          <Pressable onPress={handleBack} style={styles.backButton} accessibilityRole="button">
            <Ionicons name="chevron-back" size={icon.lg} color={color.textPrimary} />
          </Pressable>
          <Text style={styles.title}>Usage Analytics</Text>
          <View style={styles.spacer} />
        </View>

        {stats && (
          <>
            {/* USER METRICS */}
            <View style={styles.section}>
              <SectionHeader label="User Activity" />
              <ListCard>
                <MetricRow label="Total Users" value={stats.users.total_users} />
                <MetricRow label="Active (30 days)" value={stats.users.active_users_30d} highlight />
                <MetricRow label="New This Month" value={stats.users.new_users_30d} delta />
                <MetricRow label="Never Logged In" value={stats.users.never_logged_in} warn={stats.users.never_logged_in > 0} />
                <MetricRow label="Verified Emails" value={stats.users.verified_users} hideBorder />
              </ListCard>
            </View>

            {/* GROWTH CHART */}
            {stats.userGrowth.length > 0 && (
              <View style={styles.section}>
                <SectionHeader label="User Growth (12 months)" />
                <View style={styles.chartContainer}>
                  <GrowthChart data={stats.userGrowth} />
                </View>
              </View>
            )}

            {/* OPPORTUNITY METRICS */}
            <View style={styles.section}>
              <SectionHeader label="Opportunities" />
              <ListCard>
                <MetricRow label="Total Opportunities" value={stats.properties.total_properties} />
                <MetricRow label="Evaluating" value={stats.properties.research_properties} />
                <MetricRow label="Under Contract" value={stats.properties.under_contract_properties} />
                <MetricRow label="Owned" value={stats.properties.owned_properties} highlight />
                <MetricRow label="Listed for Sale" value={stats.properties.listed_properties} />
                <MetricRow label="Sold" value={stats.properties.sold_properties} hideBorder />
              </ListCard>
            </View>

            {/* PORTFOLIO METRICS */}
            <View style={styles.section}>
              <SectionHeader label="Portfolios" />
              <ListCard>
                <MetricRow label="Total Portfolios" value={stats.portfolios.total_portfolios} />
                <MetricRow label="New This Month" value={stats.portfolios.new_portfolios_30d} delta hideBorder />
              </ListCard>
            </View>

            {/* FINANCIAL METRICS */}
            <View style={styles.section}>
              <SectionHeader label="Financial Activity" />
              <ListCard>
                <MetricRow label="Total Entries" value={stats.financial.total_entries} />
                <MetricRow label="Income Entries" value={stats.financial.income_entries} />
                <MetricRow label="Expense Entries" value={stats.financial.expense_entries} />
                <MetricRow label="Total Income" value={formatCurrency(stats.financial.total_income) || '$0'} isText highlight />
                <MetricRow label="Total Expenses" value={formatCurrency(stats.financial.total_expenses) || '$0'} isText />
                <MetricRow label="Property Value" value={formatCurrency(stats.properties.total_property_value) || '$0'} isText hideBorder />
              </ListCard>
            </View>

            {/* ACCOUNT REQUESTS */}
            <View style={styles.section}>
              <SectionHeader label="Account Requests" />
              <ListCard>
                <MetricRow label="Total Requests" value={stats.accountRequests.total_requests} />
                <MetricRow label="Pending" value={stats.accountRequests.pending_requests} warn={stats.accountRequests.pending_requests > 0} />
                <MetricRow label="Approved" value={stats.accountRequests.approved_requests} />
                <MetricRow label="Denied" value={stats.accountRequests.denied_requests} hideBorder />
              </ListCard>
            </View>
          </>
        )}
      </ScrollView>
    </ScreenContainer>
  );
}

// =============================================================================
// METRIC ROW
// =============================================================================

interface MetricRowProps {
  label: string;
  value: number | string;
  highlight?: boolean;
  delta?: boolean;
  warn?: boolean;
  isText?: boolean;
  hideBorder?: boolean;
}

function MetricRow({ label, value, highlight, delta, warn, isText, hideBorder }: MetricRowProps) {
  const displayValue = isText ? value : typeof value === 'number' ? value.toLocaleString() : value;
  
  return (
    <View style={[styles.metricRow, !hideBorder && styles.metricRowBorder]}>
      <Text style={styles.metricLabel}>{label}</Text>
      <Text style={[
        styles.metricValue,
        highlight && styles.metricValueHighlight,
        delta && styles.metricValueDelta,
        warn && styles.metricValueWarn,
      ]}>
        {delta && typeof value === 'number' && value > 0 ? '+' : ''}{displayValue}
      </Text>
    </View>
  );
}

// =============================================================================
// GROWTH CHART (Simple bar chart)
// =============================================================================

function GrowthChart({ data }: { data: UserGrowthPoint[] }) {
  const maxCount = Math.max(...data.map(d => d.count), 1);
  
  return (
    <View style={styles.chart}>
      <View style={styles.chartBars}>
        {data.slice(-6).map((point, index) => {
          const height = (point.count / maxCount) * 80;
          const monthLabel = new Date(point.month).toLocaleDateString('en-US', { month: 'short' });
          
          return (
            <View key={index} style={styles.chartBarContainer}>
              <View style={styles.chartBarWrapper}>
                <View style={[styles.chartBar, { height: Math.max(height, 4) }]} />
              </View>
              <Text style={styles.chartBarLabel}>{monthLabel}</Text>
              <Text style={styles.chartBarValue}>{point.count}</Text>
            </View>
          );
        })}
      </View>
    </View>
  );
}

// =============================================================================
// STYLES
// =============================================================================

const styles = StyleSheet.create({
  scroll: { flex: 1 },
  scrollContent: { paddingBottom: layout.scrollBottomPadding },
  header: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: space.sm, paddingVertical: space.sm },
  backButton: { width: touch.minimum, height: touch.minimum, alignItems: 'center', justifyContent: 'center' },
  title: { flex: 1, fontSize: text.body.fontSize, fontWeight: text.bodyMedium.fontWeight, color: color.textPrimary, textAlign: 'center' },
  spacer: { width: touch.minimum },
  section: { paddingHorizontal: space.md, marginBottom: space.lg },
  metricRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: space.sm + space.xs, paddingHorizontal: space.md },
  metricRowBorder: { borderBottomWidth: border.width.thin, borderBottomColor: color.divider },
  metricLabel: { fontSize: text.body.fontSize, color: color.textSecondary },
  metricValue: { fontSize: text.body.fontSize, fontWeight: text.bodyMedium.fontWeight, color: color.textPrimary },
  metricValueHighlight: { color: color.primary },
  metricValueDelta: { color: color.success },
  metricValueWarn: { color: color.warning },
  chartContainer: { backgroundColor: color.surface, borderRadius: radius.lg, padding: space.md, borderWidth: 1, borderColor: color.border },
  chart: { height: 140 },
  chartBars: { flex: 1, flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'space-around', paddingTop: space.md },
  chartBarContainer: { alignItems: 'center', flex: 1 },
  chartBarWrapper: { height: 80, justifyContent: 'flex-end' },
  chartBar: { width: 24, backgroundColor: color.primary, borderRadius: radius.sm },
  chartBarLabel: { fontSize: text.caption.fontSize - 1, color: color.textMuted, marginTop: space.xs },
  chartBarValue: { fontSize: text.caption.fontSize - 2, color: color.textSecondary },
});
